const DATETIME_LOCAL_REGEX = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;
const MANILA_OFFSET = '+08:00';
const MANILA_TIME_ZONE = 'Asia/Manila';

function padDatePart(value: number): string {
  return String(value).padStart(2, '0');
}

interface DateTimeParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

function parseDatetimeLocal(value: string): DateTimeParts | null {
  const match = DATETIME_LOCAL_REGEX.exec(value);
  if (!match) return null;
  const parsed = {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4]),
    minute: Number(match[5]),
  };
  const candidate = new Date(
    Date.UTC(parsed.year, parsed.month - 1, parsed.day, parsed.hour, parsed.minute, 0),
  );
  if (
    Number.isNaN(candidate.getTime()) ||
    candidate.getUTCFullYear() !== parsed.year ||
    candidate.getUTCMonth() !== parsed.month - 1 ||
    candidate.getUTCDate() !== parsed.day ||
    candidate.getUTCHours() !== parsed.hour ||
    candidate.getUTCMinutes() !== parsed.minute
  ) {
    return null;
  }
  return parsed;
}

function formatDatetimeLocalFromUtcDate(date: Date): string {
  return `${date.getUTCFullYear()}-${padDatePart(date.getUTCMonth() + 1)}-${padDatePart(
    date.getUTCDate(),
  )}T${padDatePart(date.getUTCHours())}:${padDatePart(date.getUTCMinutes())}`;
}

export function datetimeLocalToManilaIso(value: string): string | null {
  const parsed = parseDatetimeLocal(value);
  if (!parsed) return null;
  return `${parsed.year}-${padDatePart(parsed.month)}-${padDatePart(parsed.day)}T${padDatePart(
    parsed.hour,
  )}:${padDatePart(parsed.minute)}:00${MANILA_OFFSET}`;
}

export function addDaysToDatetimeLocal(value: string, days: number): string | null {
  const parsed = parseDatetimeLocal(value);
  if (!parsed || !Number.isInteger(days)) return null;
  const candidate = new Date(
    Date.UTC(parsed.year, parsed.month - 1, parsed.day, parsed.hour, parsed.minute, 0),
  );
  candidate.setUTCDate(candidate.getUTCDate() + days);
  return formatDatetimeLocalFromUtcDate(candidate);
}

export function manilaIsoToDatetimeLocal(value: string | null | undefined): string {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: MANILA_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(parsed);
  const byType = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  const year = byType.year;
  const month = byType.month;
  const day = byType.day;
  const hour = byType.hour;
  const minute = byType.minute;
  if (!year || !month || !day || !hour || !minute) return '';
  return `${year}-${month}-${day}T${hour}:${minute}`;
}

export const DEAL_DURATION_DAY_PRESETS = [1, 3, 7, 14, 30] as const;
